import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  combineLatest,
  defaultIfEmpty,
  defer,
  filter,
  finalize,
  first,
  from,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  shareReplay,
  skip,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
  zip,
} from 'rxjs';

import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ToastService } from 'app/shared/services/toast.service';
import { TranslationService } from 'app/shared/services/translation.service';
import { TreeItemLike } from '../../../tree-list/tree-list.component';
import _ from 'lodash';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { TocService } from '../../toc.service';
import { IdentifyService } from './identify.service';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';
import { LayersInfoService } from '../../layers.service';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import {
  EsriUtils,
  LayerWithChildren,
  QueryableLayer,
} from 'app/shared/utils/esri.utils';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import RelationshipQuery from '@arcgis/core/rest/support/RelationshipQuery';
import { ExportService } from 'app/shared/services/export.service';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { HighlightConfigGroupLike } from '../highlight-config-form/highlight-config-form.component';
import { defaultHighlightConfig } from 'app/shared/constants/highlight.constant';
import { HighlightService } from '../../highlight.service';
import {
  BufferConfigGroupLike,
  BufferService,
  defaultBufferConfig,
} from '../../buffer.service';
import { onSubscribe } from '@rxjs-lifecycle/core';
import { ScreenService } from 'app/shared/services/screen.service';

interface PropertyValueLike {
  label: string;
  value: any;
  field: __esri.Field;
  layer?: QueryableLayer;
}

interface CustomTreeItemLike extends TreeItemLike<CustomTreeItemLike> {
  displayFieldValue?: any;
  displayField?: __esri.Field;
  layer?: QueryableLayer;
  graphic?: __esri.Graphic;
  objectId?: number;
  propertiesObservable?: Observable<PropertyValueLike[]>;
  canShowAttachmentsObservable?: Observable<boolean>;
  relationshipListObservable?: Observable<any[]>;
  canShowRelationshipListObservable?: Observable<boolean>;
  loadingController?: LoadingController;
}

@Component({
  selector: 'app-identify',
  templateUrl: 'identify.component.html',
  styleUrls: ['identify.component.scss'],
  providers: [IdentifyService],
})
export class IdentifyComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('treeItemTpl', { static: true })
  treeItemTpl: TemplateRef<any>;
  @ViewChild('treePropertiesTpl', { static: true })
  treePropertiesTpl: TemplateRef<any>;

  @Input()
  mapView: __esri.MapView;

  @Input()
  point: __esri.Point;

  treeList: CustomTreeItemLike[];

  get hasGraphicResults() {
    return !!this.treeList?.some((item) => item.items?.length);
  }

  private graphicLayerSelectedMap = new Map<
    QueryableLayer,
    Set<__esri.Graphic>
  >();

  get hasGraphicsSelected() {
    if (this.graphicLayerSelectedMap?.size) {
      for (const set of this.graphicLayerSelectedMap.values()) {
        if (set?.size) {
          return true;
        }
      }
    }

    return false;
  }

  get graphicListSelected() {
    if (this.graphicLayerSelectedMap?.size) {
      return Array.from(this.graphicLayerSelectedMap.values()).flatMap((x) =>
        Array.from(x)
      );
    }

    return [];
  }

  get isBtnBufferDisabled() {
    return !this.hasGraphicsSelected;
  }

  get isBtnExportDisabled() {
    return !this.hasGraphicsSelected;
  }

  get isBtnExportXlsxVisible() {
    return !this.appConfig.externalMode;
  }

  get isBtnExportKmlVisible() {
    return !this.appConfig.externalMode;
  }

  get isBtnExportShpVisible() {
    return !this.appConfig.externalMode;
  }

  allGraphicsSelected = false;

  loadingController = new LoadingController();

  private highlightGraphicsSubject = new Subject<{
    layer: QueryableLayer;
    changes: { adds?: __esri.Graphic[]; deletes?: __esri.Graphic[] };
  }>();

  highlightConfigModalVisible = false;
  highlightConfigGroup: HighlightConfigGroupLike = defaultHighlightConfig;

  private highlightConfigSubject = new Subject<void>();

  bufferFormModalVisible: boolean;

  private bufferConfigSubject = new Subject<void>();
  bufferConfigGroup: BufferConfigGroupLike = defaultBufferConfig;

  // Translation getters
  get bufferText(): string {
    return this.translationService.getCurrentLanguage()['buffer'];
  }

  get symbologyText(): string {
    return this.translationService.getCurrentLanguage()['symbology'];
  }

  get zoomText(): string {
    return this.translationService.getCurrentLanguage()['zoom'];
  }

  get selectAllText(): string {
    return this.translationService.getCurrentLanguage()['select-all'];
  }

  get clearText(): string {
    return this.translationService.getCurrentLanguage()['clear-id'];
  }

  get clearSelectionText(): string {
    return this.translationService.getCurrentLanguage()['clear'];
  }

  get accessHereText(): string {
    return this.translationService.getCurrentLanguage()['access-here'];
  }

  private featureLayerObservableCache = new Map<
    QueryableLayer,
    {
      shouldDestroy: boolean;
      observable: Observable<__esri.FeatureLayer>;
      featureLayer?: __esri.FeatureLayer;
    }
  >();

  private featureLayerCacheByLayerUrl = new Map<
    string,
    {
      shouldDestroy?: boolean;
      observable: Observable<__esri.FeatureLayer>;
      layer?: QueryableLayer | LayerWithChildren;
    }
  >();

  private mapViewSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private identifyService: IdentifyService,
    private mainMapService: MainMapService,
    private layersInfoService: LayersInfoService,
    private tocService: TocService,
    private highlightService: HighlightService,
    private bufferService: BufferService,
    private exportService: ExportService,
    private appConfig: AppConfigService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.reset();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.highlightGroupLayer || changes.point) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();

    for (let holder of this.featureLayerObservableCache.values()) {
      if (holder.shouldDestroy) {
        EsriUtils.destroyLayersRecursive(holder.featureLayer);
      }
    }

    this.featureLayerObservableCache.clear();

    for (let holder of this.featureLayerCacheByLayerUrl.values()) {
      if (holder.shouldDestroy) {
        EsriUtils.destroyLayersRecursive(holder.layer);
      }
    }

    this.featureLayerCacheByLayerUrl.clear();

    this.treeList = null;
  }

  onHighlightConfigChanged() {
    this.highlightConfigSubject.next();
  }

  onGraphicItemSelected(item: CustomTreeItemLike) {
    const layer = item.layer;
    if (!layer) {
      return;
    }

    const graphic = item.graphic;
    this.toggleSelectionGraphic(layer, graphic);
  }

  onBtnToggleSelectAllClick() {
    this.allGraphicsSelected = !this.allGraphicsSelected;
    if (this.allGraphicsSelected) {
      this.selectAllGraphics();
    } else {
      this.unselectAllGraphics();
    }
  }

  onBtnZoomToClick() {
    if (!this.hasGraphicsSelected) {
      return;
    }

    const geometryList = this.graphicListSelected.map((x) => x.geometry);
    this.mainMapService.goTo(geometryList);
  }

  onBtnClearClick() {
    this.reset();
  }

  onBtnExportXlsxClick() {
    this.exportData((fields, features) => {
      features = features.map(({ attributes }) => {
        return { attributes };
      }) as any;
      return this.exportService.exportAsXlsxLink({
        fields,
        features,
      });
    });
  }

  onBtnExportKmlClick() {
    this.exportData(
      (fields, features) =>
        this.exportService.exportAsKmlLink({
          fields,
          features,
        }),
      this.appConfig.exportLimit
    );
  }

  onBtnExportShpClick() {
    this.exportData(
      (fields, features) =>
        this.exportService.exportAsShpLink({
          fields,
          features,
        }),
      this.appConfig.exportLimit
    );
  }

  onBtnBufferClick() {
    this.bufferFormModalVisible = true;
  }

  onBufferConfigChanged() {
    this.bufferConfigSubject.next();
  }

  onBtnHighlightConfigClick() {
    this.highlightConfigModalVisible = true;
  }

  isFieldUrl(field: __esri.Field) {
    return !!field?.name?.toLowerCase()?.includes('url');
  }

  private reset() {
    this.mapViewSubscription?.unsubscribe();
    this.treeList = null;
    this.allGraphicsSelected = false;
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView || !this.point) {
      return;
    }

    const subscription = new Subscription();

    const allLayers = this.tocService
      .getLayerMenuItems()
      .flatMap((menuItem) => {
        return EsriUtils.flatLayers(this.tocService.getLayersByItem(menuItem))
          .filter(
            (layer) =>
              (EsriUtils.isQueryableLayer(layer) &&
                !EsriUtils.isSubLayer(layer)) ||
              layer instanceof MapImageLayer
          )
          .filter((layer) => {
            return (
              (layer as __esri.FeatureLayer).capabilities?.operations
                ?.supportsQuery !== false && EsriUtils.isLoaded(layer)
            );
          })
          .reverse();
      });

    this.loadingController.show();
    subscription.add(
      this.identifyService
        .hitTest(this.mapView, this.point, allLayers)
        .pipe(
          finalize(() => {
            this.loadingController.hide();
          })
        )
        .subscribe({
          next: (results) => {
            if (!results.length) {
              this.toastService.warning(
                this.translationService.getCurrentLanguage()['no-records-found']
              );
            }

            this.handleResults(results);
            subscription.add(this.getHighlightObservable(results).subscribe());
            subscription.add(this.getBufferObservable(results).subscribe());
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.translationService.getCurrentLanguage()['failed-fetch-data']
            );
          },
        })
    );

    this.mapViewSubscription = subscription;
  }

  private handleResults(
    results: { layer: QueryableLayer; list: __esri.Graphic[] }[]
  ) {
    if (results.length) results = results.filter((x) => x.layer.fields);

    if (!results.length) {
      this.treeList = null;
      return;
    }

    const observableList = results.map(({ layer, list }) => {
      const title = this.tocService.getPathLayersByLayerAsText(layer);

      return zip(
        list.map((graphic) => {
          return this.parseGraphicToTreeItem(layer, graphic).pipe(
            tap((item) => {
              const isSelectedFn = () => {
                return this.isGraphicSelected(layer, graphic);
              };

              Object.defineProperty(item, 'selected', {
                get: isSelectedFn,
              });

              this.setupTreeItem(item);
            })
          );
        })
      ).pipe(
        defaultIfEmpty([] as CustomTreeItemLike[]),
        map((items): CustomTreeItemLike => {
          return {
            itemTemplate: this.treeItemTpl,
            text: `${title} (${items.length})`,
            items,
            layer,
          };
        })
      );
    });

    this.loadingController.show();
    zip(observableList)
      .pipe(
        defaultIfEmpty([] as CustomTreeItemLike[]),
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.treeList = list;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-fetch-data']
          );
        },
      });
  }

  private getLayerRelationshipListObservable(
    layer: QueryableLayer,
    graphic: __esri.Graphic
  ): Observable<CustomTreeItemLike[]> {
    return this.getFeatureLayerByLayer(layer).pipe(
      mergeMap((featureLayer) => {
        if (!featureLayer?.relationships?.length) {
          return of([]);
        }

        const objectId = graphic.attributes[layer.objectIdField];

        return zip(
          featureLayer.relationships.map((rel) => {
            const query = new RelationshipQuery({
              relationshipId: rel.id,
              objectIds: [objectId],
              outFields: ['*'],
              returnGeometry: true,
            });

            const layerUrl: string = EsriUtils.resolveLayerUrl(
              featureLayer.url,
              rel.relatedTableId
            );

            return EsriUtils.queryRelatedFeatures(featureLayer, query).pipe(
              map((res) => res?.[objectId]),
              mergeMap((featureSet) => {
                if (!featureSet?.features?.length) {
                  return of(null);
                }

                const featureLayerRelObservable =
                  this.getFeatureLayerRelatedByUrl(layerUrl);

                return combineLatest([
                  featureLayerRelObservable,
                  this.layersInfoService.getLayerTitle(layerUrl),
                  ...featureSet.features.map((graphic) => {
                    return this.parseGraphicToTreeItemByEsriUrl(
                      layerUrl,
                      graphic,
                      featureLayerRelObservable
                    );
                  }),
                ]).pipe(
                  defaultIfEmpty([] as CustomTreeItemLike[]),
                  map(([featureLayerRel, layerTitle, ...items]) => {
                    return {
                      itemTemplate: this.treeItemTpl,
                      text: `${layerTitle} (${items.length})`,
                      items,
                      layer: featureLayerRel,
                    };
                  })
                );
              })
            );
          })
        ).pipe(
          map((list) => list.filter((x) => !!x)),
          defaultIfEmpty([] as CustomTreeItemLike[])
        );
      })
    );
  }

  private getFeatureLayerByLayer(layer: QueryableLayer) {
    if (![FeatureLayer, Sublayer].some((x) => layer instanceof x)) {
      return of(null);
    }

    let featureLayerObservable: Observable<__esri.FeatureLayer> =
      this.featureLayerObservableCache.get(layer)?.observable;
    if (!featureLayerObservable) {
      let shouldDestroy: boolean;
      if (layer instanceof Sublayer) {
        shouldDestroy = true;
        featureLayerObservable = defer(() => {
          return from(
            layer.createFeatureLayer().then((featureLayer) => {
              const holder = this.featureLayerObservableCache.get(layer);
              if (holder) {
                holder.featureLayer = featureLayer;
              } else {
                EsriUtils.destroyLayersRecursive(featureLayer);
              }

              return featureLayer;
            })
          );
        });
      } else {
        shouldDestroy = false;
        featureLayerObservable = of(layer as any);
      }

      featureLayerObservable = featureLayerObservable.pipe(
        mergeMap((featureLayer) => {
          return EsriUtils.load(featureLayer).pipe(map(() => featureLayer));
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );

      this.featureLayerObservableCache.set(layer, {
        shouldDestroy,
        observable: featureLayerObservable,
      });
    }

    return featureLayerObservable;
  }

  private getFeatureLayerRelatedByUrl(
    layerUrl: string
  ): Observable<__esri.FeatureLayer> {
    let holder = this.featureLayerCacheByLayerUrl.get(layerUrl);
    if (!holder) {
      const hasLayerWithUrl = (layer: QueryableLayer) => {
        return !!layer?.url?.includes(layerUrl);
      };

      const observable = defer(() => {
        const res = this.tocService.findLayerMenu((layer) => {
          return hasLayerWithUrl(layer as any);
        });

        type Res = {
          layer: __esri.Sublayer | __esri.FeatureLayer;
          shouldDestroy: boolean;
        };
        let layerObservable: Observable<Res>;

        if (res?.layer) {
          layerObservable = of({
            layer: res.layer as any,
            shouldDestroy: false,
          } as Res);
        } else {
          layerObservable = this.layersInfoService.getPortalItem(layerUrl).pipe(
            mergeMap((portalItem) => {
              let layerObservable: Observable<any>;
              if (portalItem) {
                layerObservable = EsriUtils.loadLayerFromPortalItem(portalItem);
              } else {
                const layer = new FeatureLayer({
                  url: layerUrl,
                });

                layerObservable = EsriUtils.load(layer).pipe(map(() => layer));
              }

              return layerObservable.pipe(
                map((layer) => {
                  return {
                    layer,
                    shouldDestroy: true,
                  } as Res;
                })
              );
            })
          );
        }

        return layerObservable;
      }).pipe(
        mergeMap(({ layer, shouldDestroy }) => {
          let layerTarget: __esri.FeatureLayer | __esri.Sublayer;
          if (
            EsriUtils.isLayerWithCollection(layer) &&
            !hasLayerWithUrl(layer)
          ) {
            layerTarget = EsriUtils.findRecursiveLayer(layer, (x) => {
              return hasLayerWithUrl(x as any);
            }) as any;
          }

          const holder = this.featureLayerCacheByLayerUrl.get(layerUrl);
          holder.layer = layer;
          holder.shouldDestroy = shouldDestroy;

          return this.getFeatureLayerByLayer(layerTarget ?? layer);
        }),
        shareReplay({
          refCount: true,
          bufferSize: 1,
        })
      );

      holder = {
        observable,
      };
      this.featureLayerCacheByLayerUrl.set(layerUrl, holder);
    }

    return holder.observable;
  }

  private getHighlightObservable(
    results: { layer: QueryableLayer; list: __esri.Graphic[] }[]
  ): Observable<any> {
    return new Observable((subscriber) => {
      if (!results?.length) {
        return;
      }

      results.forEach(({ layer, list }) => {
        if (!list?.length) {
          return;
        }

        const subscription = new Subscription();

        const highlightSubscriptionMap = new Map<
          __esri.Graphic,
          Subscription
        >();

        subscription.add(
          merge(
            this.highlightGraphicsSubject.pipe(
              filter((x) => x?.layer === layer),
              map((x) => x.changes)
            ),
            this.highlightConfigSubject.pipe(
              map(() => {
                const graphics = Array.from(highlightSubscriptionMap.keys());
                return { adds: graphics, deletes: graphics };
              })
            )
          ).subscribe((changes) => {
            changes.deletes?.forEach((graphic) => {
              highlightSubscriptionMap.get(graphic)?.unsubscribe();
              highlightSubscriptionMap.delete(graphic);

              this.graphicLayerSelectedMap.get(layer)?.delete(graphic);
            });

            changes.adds?.forEach((graphic) => {
              let graphicsSet = this.graphicLayerSelectedMap.get(layer);
              if (!graphicsSet) {
                graphicsSet = new Set();
                this.graphicLayerSelectedMap.set(layer, graphicsSet);
              }

              if (graphicsSet.has(graphic)) {
                return;
              }

              graphicsSet.add(graphic);

              const highlightSubscription = this.highlightService
                .highlight(graphic.geometry, this.highlightConfigGroup)
                .subscribe();
              highlightSubscriptionMap.set(graphic, highlightSubscription);
            });
          })
        );

        subscription.add(() => {
          for (const x of highlightSubscriptionMap.values()) {
            x.unsubscribe();
          }

          highlightSubscriptionMap.clear();

          this.graphicLayerSelectedMap.delete(layer);
        });

        subscriber.add(subscription);
      });
    });
  }

  private getBufferObservable(
    results: { layer: QueryableLayer; list: __esri.Graphic[] }[]
  ): Observable<any> {
    return new Observable((subscriber) => {
      if (!results?.length) {
        return;
      }

      results.forEach(({ layer, list }) => {
        if (!list?.length) {
          return;
        }

        const subscription = new Subscription();

        const bufferSubscriptionMap = new Map<__esri.Graphic, Subscription>();

        subscription.add(
          merge(
            this.bufferConfigSubject.pipe(
              first(),
              map(() => {
                return { adds: this.graphicListSelected };
              })
            ),
            this.bufferConfigSubject.pipe(
              skip(1),
              map(() => {
                const graphics = Array.from(bufferSubscriptionMap.keys());
                return { adds: graphics, deletes: graphics };
              })
            )
          ).subscribe(
            (changes: {
              adds?: __esri.Graphic[];
              deletes?: __esri.Graphic[];
            }) => {
              changes.deletes?.forEach((graphic) => {
                bufferSubscriptionMap.get(graphic)?.unsubscribe();
                bufferSubscriptionMap.delete(graphic);
              });

              changes.adds?.forEach((graphic) => {
                const bufferSubscription = this.bufferService
                  .buffer(graphic.geometry, this.bufferConfigGroup)
                  .subscribe();
                bufferSubscriptionMap.set(graphic, bufferSubscription);
              });
            }
          )
        );

        subscription.add(() => {
          for (const x of bufferSubscriptionMap.values()) {
            x.unsubscribe();
          }

          bufferSubscriptionMap.clear();
        });

        subscriber.add(subscription);
      });
    });
  }

  private parseGraphicToTreeItem(
    layer: QueryableLayer,
    graphic: __esri.Graphic
  ) {
    const fieldsObservable: Observable<__esri.Field[]> = defer(() => {
      return of(layer.fields);
    });

    let displayFieldObservable: Observable<__esri.Field>;
    if (layer instanceof Sublayer) {
      const layerUrl = EsriUtils.resolveLayerUrl(layer.url, layer.id);
      displayFieldObservable = this.layersInfoService.getDisplayField(layerUrl);
    } else {
      const displayField =
        layer.fields.find((x) => x.name === layer.displayField) ??
        layer.fields.find((x) => x.name === layer.objectIdField);
      displayFieldObservable = of(displayField);
    }

    const objectIdFieldNameObservable = of(layer.objectIdField);

    return this.parseGraphicToTreeItemCustom(graphic, layer, {
      layerUrlObservable: of(layer.url),
      fieldsObservable,
      displayFieldObservable,
      objectIdFieldNameObservable,
    });
  }

  private parseGraphicToTreeItemByEsriUrl(
    layerUrl: string,
    graphic: __esri.Graphic,
    layer: QueryableLayer | Observable<QueryableLayer>
  ) {
    const fieldsObservable = this.layersInfoService.getFields(layerUrl);
    const displayFieldObservable =
      this.layersInfoService.getDisplayField(layerUrl);
    const objectIdFieldNameObservable =
      this.layersInfoService.getObjectIdFieldName(layerUrl);

    return this.parseGraphicToTreeItemCustom(graphic, layer, {
      layerUrlObservable: of(layerUrl),
      fieldsObservable,
      displayFieldObservable,
      objectIdFieldNameObservable,
    });
  }

  private parseGraphicToTreeItemCustom(
    graphic: __esri.Graphic,
    layer: QueryableLayer | Observable<QueryableLayer>,
    cfg: {
      layerUrlObservable: Observable<string>;
      fieldsObservable: Observable<__esri.Field[]>;
      displayFieldObservable: Observable<__esri.Field>;
      objectIdFieldNameObservable: Observable<string>;
    }
  ) {
    if (!(layer instanceof Observable)) {
      layer = of(layer);
    }

    const propertiesObservable = combineLatest([layer, cfg.fieldsObservable])
      .pipe(
        map(([layer, fields]) => {
          return fields
            .filter(
              (field) =>
                !geomFieldsIgnored.includes(field.name) &&
                !systemFieldTypesIgnored.includes(field.type)
            )
            .map<PropertyValueLike>((field) => {
              const value = graphic.attributes[field.name];

              return {
                label: field.alias ?? field.name,
                value,
                field,
                layer,
              };
            });
        })
      )
      .pipe(
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );

    return combineLatest([
      layer,
      cfg.layerUrlObservable,
      cfg.displayFieldObservable,
      cfg.objectIdFieldNameObservable,
    ]).pipe(
      map(([layer, layerUrl, displayField, objectIdFieldName]) => {
        let objectId = graphic.attributes?.[objectIdFieldName];

        if (typeof objectId === 'string') {
          objectId = !objectId ? null : Number(objectId);
        }

        const displayFieldValue = graphic.attributes[displayField.name];

        const loadingController = new LoadingController();

        const item: CustomTreeItemLike = {
          itemTemplate: this.treeItemTpl,
          subListTemplate: this.treePropertiesTpl,
          hasItems: true,
          displayFieldValue,
          displayField,
          layer,
          graphic,
          objectId,
          propertiesObservable,
          loadingController,
        };

        item.canShowAttachmentsObservable = this.layersInfoService
          .hasAttachments(layerUrl)
          .pipe(
            onSubscribe(() => {
              loadingController.show();
            }),
            finalize(() => {
              loadingController.hide();
            })
          );

        return item;
      })
    );
  }

  private setupTreeItem(item: CustomTreeItemLike) {
    if (!item?.layer || !item?.graphic) {
      return;
    }

    const { loadingController } = item;

    const relationshipListObservable = this.getLayerRelationshipListObservable(
      item.layer,
      item.graphic
    ).pipe(
      onSubscribe(() => {
        loadingController.show();
      }),
      finalize(() => {
        loadingController.hide();
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
    item.relationshipListObservable = relationshipListObservable;
    item.canShowRelationshipListObservable = relationshipListObservable.pipe(
      map((res) => !!res?.length)
    );
  }

  private isGraphicSelected(layer: QueryableLayer, graphic: __esri.Graphic) {
    return !!this.graphicLayerSelectedMap.get(layer)?.has(graphic);
  }

  private toggleSelectionGraphic(
    layer: QueryableLayer,
    graphic: __esri.Graphic
  ) {
    if (this.isGraphicSelected(layer, graphic)) {
      this.unselectGraphic(layer, graphic);
    } else {
      this.selectGraphic(layer, graphic);
    }
  }

  private selectGraphic(layer: QueryableLayer, graphic: __esri.Graphic) {
    this.highlightGraphicsSubject.next({
      layer,
      changes: { adds: [graphic] },
    });
  }

  private unselectGraphic(layer: QueryableLayer, graphic: __esri.Graphic) {
    this.highlightGraphicsSubject.next({
      layer,
      changes: { deletes: [graphic] },
    });
  }

  private selectAllGraphics() {
    this.treeList?.forEach((item) => {
      const layer = item.layer;
      const graphics = item.items.map((x) => x.graphic);

      this.highlightGraphicsSubject.next({
        layer,
        changes: { adds: graphics },
      });
    });
  }

  private unselectAllGraphics() {
    const mapEntries = Array.from(this.graphicLayerSelectedMap);
    for (const [layer, set] of mapEntries) {
      if (set.size) {
        const graphics = Array.from(set);

        this.highlightGraphicsSubject.next({
          layer,
          changes: { deletes: graphics },
        });
      }
    }
  }

  private exportData(
    fn: (
      fields: __esri.Field[],
      features: __esri.Graphic[]
    ) => Observable<string>,
    limit?: number
  ) {
    if (this.globalLoadingController.isShown() || this.isBtnExportDisabled) {
      return;
    }

    let shouldShowMessage = false;
    const exportObservableList = Array.from(
      this.graphicLayerSelectedMap,
      ([layer, set]) => {
        let features = Array.from(set);
        if (typeof limit === 'number' && features.length > limit) {
          features = features.slice(0, limit);
          shouldShowMessage = true;
        }

        const fields = layer.fields;
        return fn(fields, features);
      }
    );

    if (shouldShowMessage) {
      this.toastService.info(
        this.translationService.getCurrentLanguage()['export-large-items']
      );
    }

    this.globalLoadingController.show();
    zip(exportObservableList)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (links) => {
          links.forEach((link) => {
            window.open(link, '_blank');
          });
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-export-data']
          );
        },
      });
  }
}
